// Einstieg: Link auswerten, Aufnahme starten oder fortsetzen.
//   /b/<slug>    fester Link eines Betriebs   → neue Aufnahme (oder die begonnene)
//   /i/<token>   persönliche Einladung        → immer dieselbe Aufnahme
//   /s/<token>   Fortsetzen                   → Aufnahme direkt
//   /            Kunden-Version (ohne Firma)  → E-Mail + Code, dann eigene Aufnahme
//   /k/<link>    Link aus der Code-Mail       → eigene Aufnahme
//   /u/<token>   Übernahme-Anfrage einer Firma → zustimmen oder ablehnen
// Danach steht in der Adresszeile immer /s/<token>, damit Neuladen und
// "Später weitermachen" funktionieren.

import { useEffect, useState } from "react";
import { DEMO_COMPANY_SLUG } from "@umzugsscan/shared";
import { ApiError, NetworkError, api } from "./api.ts";
import { Erfassung } from "./Erfassung.tsx";
import { StoreContext } from "./hooks.ts";
import { forgetLink, openLocalDb, rememberLink, tokenForLink } from "./speicher.ts";
import { DraftStore } from "./store.ts";
import { errorText } from "./texte.ts";
import { RechtLinks } from "./ui/Recht.tsx";
import { WillkommenAnsicht } from "./schritte/Willkommen.tsx";
import { AufsHandy } from "./ui/AufsHandy.tsx";
import { PrivatStart } from "./schritte/PrivatStart.tsx";
import { Uebernahme } from "./schritte/Uebernahme.tsx";

type Branding = { name: string; logoUrl: string | null; photoRetentionDays?: number; demo?: boolean };
type Boot =
  | { kind: "laden" }
  | { kind: "fehler"; title: string; text: string }
  | { kind: "vorstart"; slug: string; branding: Branding }
  | { kind: "privatstart"; notice?: string | null }
  | { kind: "bereit"; store: DraftStore };

class LinkFehler extends Error {}

/**
 * Rückkehr von der Zahlungsseite (?zahlung=ok&sid=…) beim Umschreiben der Adresse behalten:
 * Die Ergebnisseite liest sie erst nach dem Laden (12.10.2026, vorher zeigte sie wieder das Bezahlformular).
 */
function zahlungQuery(): string {
  const q = new URLSearchParams(window.location.search);
  if (!q.get("zahlung")) return "";
  const keep = new URLSearchParams();
  for (const k of ["zahlung", "sid"]) if (q.get(k)) keep.set(k, q.get(k)!);
  return `?${keep}`;
}

function linkParts() {
  if (window.location.pathname.replace(/\/$/, "") === "/demo") return { kind: "b" as const, value: DEMO_COMPANY_SLUG };
  const [, kind, raw] = window.location.pathname.split("/");
  const value = raw ? decodeURIComponent(raw) : "";
  if (!value || (kind !== "b" && kind !== "i" && kind !== "s")) throw new LinkFehler();
  return { kind, value } as { kind: "b" | "i" | "s"; value: string };
}

/** Aufnahme öffnen; gemerkte, inzwischen gelöschte Aufnahme → neu beginnen. */
async function openStore(kind: "b" | "i" | "s", value: string, token: string): Promise<DraftStore> {
  const db = await openLocalDb();
  let store: DraftStore;
  try {
    store = await DraftStore.open(token, db, api);
  } catch (e) {
    if (kind !== "s" && e instanceof ApiError && e.code === "nicht_gefunden") {
      forgetLink(kind, value);
      const started = await api.start<{ token: string }>(kind === "b" ? { slug: value } : { invite: value });
      rememberLink(kind, value, started.token);
      store = await DraftStore.open(started.token, db, api);
    } else throw e;
  }
  window.history.replaceState(null, "", `/s/${encodeURIComponent(store.getSnapshot().token)}${zahlungQuery()}`);
  return store;
}

/**
 * Fester Firmenlink ohne begonnene Aufnahme: nur die öffentlichen Firmendaten
 * laden. Die Aufnahme entsteht erst mit "Los geht's" (Live-Durchlauf
 * 08.10.2026: jeder Aufruf legte einen leeren Entwurf an).
 */
/** Kunden-Version: Aufnahme zum bestätigten Token öffnen und merken. */
async function openPrivate(token: string): Promise<DraftStore> {
  rememberLink("k", "privat", token);
  const store = await DraftStore.open(token, await openLocalDb(), api);
  window.history.replaceState(null, "", `/s/${encodeURIComponent(token)}${zahlungQuery()}`);
  return store;
}

async function bootPrivate(): Promise<Boot> {
  const [, kind, raw] = window.location.pathname.split("/");
  if (kind === "k" && raw) {
    try {
      const r = await api.request<{ token: string }>("POST", "/privat/bestaetigen", { link: decodeURIComponent(raw) });
      return { kind: "bereit", store: await openPrivate(r.token) };
    } catch (e) {
      if (e instanceof ApiError && e.code === "link_ungueltig") {
        window.history.replaceState(null, "", "/");
        return { kind: "privatstart", notice: "Dieser Link ist abgelaufen oder wurde schon benutzt. Fordern Sie einfach einen neuen Code an." };
      }
      throw e;
    }
  }
  const known = tokenForLink("k", "privat");
  if (known) {
    try {
      return { kind: "bereit", store: await openPrivate(known) };
    } catch (e) {
      if (!(e instanceof ApiError && e.code === "nicht_gefunden")) throw e;
      forgetLink("k", "privat");
    }
  }
  return { kind: "privatstart" };
}

async function boot(): Promise<Boot> {
  const path = window.location.pathname;
  if (path === "/" || path === "" || path.startsWith("/k/")) return bootPrivate();
  const { kind, value } = linkParts();
  if (kind === "s") return { kind: "bereit", store: await openStore(kind, value, value) };
  const known = tokenForLink(kind, value);
  if (known) return { kind: "bereit", store: await openStore(kind, value, known) };
  if (kind === "b") return { kind: "vorstart", slug: value, branding: await api.request<Branding>("GET", `/oeffentlich/firmen/${encodeURIComponent(value)}`) };
  const started = await api.start<{ token: string }>({ invite: value });
  rememberLink(kind, value, started.token);
  return { kind: "bereit", store: await openStore(kind, value, started.token) };
}

const failure = (e: unknown): Boot => {
  console.error(e);
  if (e instanceof LinkFehler) return { kind: "fehler", title: "Link unvollständig", text: "Bitte öffnen Sie den Link direkt aus der Nachricht oder E-Mail, in der Sie ihn bekommen haben." };
  if (e instanceof NetworkError) return { kind: "fehler", title: "Keine Internetverbindung", text: "Bitte öffnen Sie den Link erneut, sobald Sie Empfang haben." };
  return { kind: "fehler", title: "Das hat nicht geklappt", text: errorText(e) };
};

function Vorstart(props: { slug: string; branding: Branding; onStarted: (s: DraftStore) => void }) {
  const [privacy, setPrivacy] = useState(false);
  const [training, setTraining] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const started = await api.start<{ token: string }>({ slug: props.slug });
      rememberLink("b", props.slug, started.token);
      const store = await openStore("b", props.slug, started.token);
      store.updateSurvey({ consentPrivacy: true, consentTraining: props.branding.demo ? false : training, photoNotice: true });
      store.go({ screen: "grundriss" });
      props.onStarted(store);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <div className="app">
      <header className="kopf">
        <div className="kopf-row">
          {props.branding.logoUrl ? (
            <img className="logo" src={props.branding.logoUrl} alt={props.branding.name} />
          ) : (
            <strong className="firma">{props.branding.name}</strong>
          )}
        </div>
      </header>
      <main className="main">
        <AufsHandy />
        <WillkommenAnsicht
          companyName={props.branding.name}
          retentionDays={props.branding.photoRetentionDays ?? 90}
          demo={props.branding.demo}
          consentPrivacy={privacy}
          consentTraining={training}
          onPrivacy={setPrivacy}
          onTraining={setTraining}
          onStart={() => void start()}
          busy={busy}
          error={error}
        />
        <RechtLinks />
      </main>
    </div>
  );
}

export function App() {
  const [, kind, raw] = window.location.pathname.split("/");
  if (kind === "u" && raw) return <Uebernahme token={decodeURIComponent(raw)} />;
  return <Erfassen />;
}

function Erfassen() {
  const [state, setState] = useState<Boot>({ kind: "laden" });

  useEffect(() => {
    // Abgebrochener Start (React StrictMode startet Effekte doppelt): dessen
    // Store anhalten, sonst arbeiten zwei Warteschlangen mit altem Stand.
    let alive = true;
    boot().then(
      (s) => {
        if (alive) setState(s);
        else if (s.kind === "bereit") s.store.sync.stop();
      },
      (e) => alive && setState(failure(e)),
    );
    return () => {
      alive = false;
    };
  }, []);

  // Sobald das Handy wieder Netz hat, sofort übertragen.
  useEffect(() => {
    if (state.kind !== "bereit") return;
    const online = () => state.store.sync.kick();
    window.addEventListener("online", online);
    return () => window.removeEventListener("online", online);
  }, [state]);

  if (state.kind === "privatstart")
    return (
      <PrivatStart
        notice={state.notice}
        onToken={(token) => {
          setState({ kind: "laden" });
          openPrivate(token).then((store) => setState({ kind: "bereit", store }), (e) => setState(failure(e)));
        }}
      />
    );
  if (state.kind === "vorstart")
    return <Vorstart slug={state.slug} branding={state.branding} onStarted={(store) => setState({ kind: "bereit", store })} />;
  if (state.kind === "laden")
    return (
      <div className="center">
        <div className="spinner" aria-label="Wird geladen" />
        <RechtLinks />
      </div>
    );
  if (state.kind === "fehler")
    return (
      <div className="center">
        <h1>{state.title}</h1>
        <p>{state.text}</p>
        {/* Wer ohne Firmenlink hier landet (App, Fortsetzen-Link), kommt mit einem Tipp zurück zum Anfang. */}
        {!/^\/(b|i)\//.test(window.location.pathname) && (
          <p>
            <a className="btn btn-primary btn-link" href="/">
              Zur Startseite
            </a>
          </p>
        )}
        <RechtLinks />
      </div>
    );
  return (
    <StoreContext.Provider value={state.store}>
      <Erfassung />
    </StoreContext.Provider>
  );
}
